import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import api, { setActiveEntityForApi } from '../services/api';

/**
 * Item 82 — active-entity state (mirrors DemoContext). The switcher sets
 * the active entity; api.js stamps X-Active-Entity on every request; the
 * backend swaps identity to that entity's scope. Choosing the DEFAULT
 * entity clears the header entirely (the no-swap fast path).
 *
 * Persisted per-login in localStorage so a refresh keeps you in the entity
 * you were working in.
 */

const EntityContext = createContext(null);

const storageKey = (userId) => `kodahfy.activeEntity.${userId}`;

export function EntityProvider({ children }) {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const authUser = useSelector((state) => state.auth.user);
  const [entities, setEntities] = useState([]);
  const [activeEntityId, setActiveEntityIdState] = useState(null); // null = default
  const [loaded, setLoaded] = useState(false);
  // 12 Sep 2026: entities loaded AND the saved entity applied to the API
  // client. Until then nothing under the provider renders, so a page opened
  // directly (F5) while in a second entity cannot fire its first requests
  // without the entity header and show the default entity's data.
  const [ready, setReady] = useState(false);

  const refreshEntities = useCallback(async () => {
    try {
      const res = await api.get('/entities');
      const list = res.data?.data?.entities || [];
      setEntities(list);
      return list;
    } catch (_) {
      setEntities([]);
      return [];
    } finally {
      setLoaded(true);
    }
  }, []);

  // Load entities + restore the persisted active entity on login
  useEffect(() => {
    if (!isAuthenticated) {
      setEntities([]);
      setActiveEntityIdState(null);
      setActiveEntityForApi(null);
      setLoaded(false);
      setReady(false);
      return;
    }
    (async () => {
      const list = await refreshEntities();
      const uid = authUser?.id;
      const saved = uid ? localStorage.getItem(storageKey(uid)) : null;
      // Restore only if still a member of that entity and it's not default
      const match = saved && list.find(e => e.id === saved && !e.is_default);
      if (match) {
        setActiveEntityIdState(match.id);
        setActiveEntityForApi(match.id);
      } else {
        setActiveEntityIdState(null);
        setActiveEntityForApi(null);
      }
      setReady(true);
    })();
  }, [isAuthenticated, authUser?.id, refreshEntities]);

  const setActiveEntity = useCallback((entityId) => {
    const uid = authUser?.id;
    const target = entities.find(e => e.id === entityId);
    // Default entity → clear (no header, no swap). An id not yet in local
    // state (e.g. just created, list refresh in flight) is trusted — the
    // backend 403s anything invalid.
    const effective = entityId == null ? null
      : (target && target.is_default) ? null
      : entityId;
    setActiveEntityIdState(effective);
    setActiveEntityForApi(effective);
    if (uid) {
      if (effective) localStorage.setItem(storageKey(uid), effective);
      else localStorage.removeItem(storageKey(uid));
    }
    // Everything on screen belongs to the previous entity — reload clean.
    // (Same approach as demo-mode entry: state surgery across every slice
    // is far riskier than one navigation.)
    window.location.href = '/dashboard';
  }, [entities, authUser?.id]);

  const activeEntity = entities.find(e => e.id === activeEntityId) || null;
  const defaultEntity = entities.find(e => e.is_default) || null;

  return (
    <EntityContext.Provider value={{
      entities,
      entitiesLoaded: loaded,
      activeEntityId,
      activeEntity,
      defaultEntity,
      isEntityActive: Boolean(activeEntityId),
      setActiveEntity,
      refreshEntities
    }}>
      {!isAuthenticated || ready ? children : null}
    </EntityContext.Provider>
  );
}

export function useEntity() {
  const ctx = useContext(EntityContext);
  if (!ctx) {
    // Render-safe default for surfaces mounted outside the provider
    return {
      entities: [], entitiesLoaded: false, activeEntityId: null,
      activeEntity: null, defaultEntity: null, isEntityActive: false,
      setActiveEntity: () => {}, refreshEntities: async () => []
    };
  }
  return ctx;
}
